import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Package, Heart, MapPin, Bell, ShieldCheck, LogOut } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { usePlatform } from '@/providers/PlatformProvider';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import type { FeatureKey } from '@/types/platform';
import { PageHeader } from '@/components/layout/PageHeader';
import { ListSkeleton } from '@/components/ui/Skeleton';
import { fetchOrders, orderKeys } from '@/api/orders';
import { accountKeys, fetchAddresses } from '@/api/account';

/**
 * Customer account dashboard — BUILD-PLAN §16, §58.
 *
 * The tiles are driven by feature state, so a section that is not built yet
 * links to its Coming Soon page rather than being hidden or, worse, linking
 * to a broken screen.
 */

interface Tile {
  to: string;
  label: string;
  description: string;
  icon: LucideIcon;
  feature?: FeatureKey;
}

const TILES: Tile[] = [
  {
    to: '/orders',
    label: 'Orders',
    description: 'Track and review your orders.',
    icon: Package,
    feature: 'orders',
  },
  {
    to: '/wishlist',
    label: 'Wishlist',
    description: 'Pieces you’ve saved for later.',
    icon: Heart,
    feature: 'wishlist',
  },
  {
    to: '/account/addresses',
    label: 'Addresses',
    description: 'Manage your delivery addresses.',
    icon: MapPin,
  },
  {
    to: '/account/notifications',
    label: 'Notifications',
    description: 'Updates and preferences.',
    icon: Bell,
    feature: 'notifications',
  },
  {
    /*
     | Points at Profile, where the password form actually lives.
     |
     | It used to link to `/account/security`, a route that does not exist —
     | so the tile promised a screen and delivered a Coming Soon page. There
     | is no second place to change a password, and inventing one to justify
     | the tile would have been the wrong fix (§86).
     */
    to: '/account/profile',
    label: 'Profile & security',
    description: 'Your details and password.',
    icon: ShieldCheck,
  },
];

export function AccountDashboardPage() {
  const { user, logout, isVerified } = useAuth();
  const { isVisible } = usePlatform();

  const tiles = TILES.filter((tile) => !tile.feature || isVisible(tile.feature));

  return (
    <section>
      <PageHeader
        eyebrow="My account"
        title={`Welcome, ${user?.name ?? ''}`}
        actions={
          <Button variant="ghost" size="sm" onClick={() => void logout()}>
            <LogOut className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
            Sign out
          </Button>
        }
      />

      {/*
        §16 requires the dashboard to SHOW recent orders and saved addresses,
        not merely link to them. A page of tiles is a menu; the point of a
        dashboard is that the answers to "where is my order" and "where will
        it go" are visible without navigating anywhere.
      */}
      <RecentActivity />

      {/*
        A persistent, actionable reminder rather than a blocking wall: an
        unverified customer can still browse, but needs to know why some
        actions are unavailable (§41).
      */}
      {!isVerified && (
        <div className="mt-8">
          <Alert tone="info">
            Please confirm your email address to unlock everything in your account.{' '}
            <Link to="/verify-email" className="underline underline-offset-4">
              Confirm now
            </Link>
          </Alert>
        </div>
      )}

      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {tiles.map(({ to, label, description, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            className="group flex flex-col gap-3 border border-line bg-surface p-6 transition-shadow hover:shadow-card"
          >
            <Icon
              className="h-5 w-5 text-accent"
              strokeWidth={1.5}
              aria-hidden="true"
            />

            <span className="font-display text-lg text-ink">{label}</span>

            <span className="text-sm leading-relaxed text-muted">
              {description}
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------------ */

/**
 * The two things a customer opens their account to check.
 *
 * Both panels remove themselves when their feature is off or their data is
 * empty-and-uninteresting, rather than rendering a heading over nothing (§86).
 */
function RecentActivity() {
  const { isVisible } = usePlatform();

  const orders = useQuery({
    queryKey: orderKeys.orders,
    queryFn: fetchOrders,
    enabled: isVisible('orders'),
  });

  const addresses = useQuery({
    queryKey: accountKeys.addresses,
    queryFn: fetchAddresses,
  });

  const recent = (orders.data ?? []).slice(0, 3);
  const defaultAddress = (addresses.data ?? []).find((entry) => entry.isDefault);

  return (
    <div className="mt-10 grid gap-8 lg:grid-cols-2">
      {isVisible('orders') && (
        <section>
          <PanelHeading title="Recent orders" to="/orders" linkLabel="All orders" />

          {orders.isPending ? (
            <ListSkeleton rows={2} />
          ) : recent.length === 0 ? (
            <p className="text-sm leading-relaxed text-muted">
              You have not placed an order yet.{' '}
              <Link to="/shop" className="text-accent underline-offset-4 hover:underline">
                Explore the collection
              </Link>
              .
            </p>
          ) : (
            <ul className="flex flex-col gap-3">
              {recent.map((order) => (
                <li key={order.orderNumber}>
                  <Link
                    to={`/orders/${order.orderNumber}/confirmation`}
                    className="flex items-center justify-between gap-4 border border-line bg-surface px-4 py-3.5 transition-colors hover:border-accent"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-ink">
                        {order.orderNumber}
                      </span>
                      <span className="mt-0.5 block text-xs text-muted">
                        {order.status?.label ?? 'Placed'}
                      </span>
                    </span>

                    {order.totals?.total && (
                      <span className="shrink-0 text-sm text-ink">
                        {order.totals.total.formatted}
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <section>
        <PanelHeading title="Default address" to="/account/addresses" linkLabel="Manage" />

        {addresses.isPending ? (
          <ListSkeleton rows={1} />
        ) : defaultAddress ? (
          <address className="border border-line bg-surface px-4 py-3.5 text-sm not-italic leading-[1.9] text-muted">
            {(defaultAddress.lines ?? []).map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </address>
        ) : (
          <p className="text-sm leading-relaxed text-muted">
            No default address yet.{' '}
            <Link
              to="/account/addresses"
              className="text-accent underline-offset-4 hover:underline"
            >
              Add one
            </Link>{' '}
            and checkout will fill itself in.
          </p>
        )}
      </section>
    </div>
  );
}

function PanelHeading({
  title,
  to,
  linkLabel,
}: {
  title: string;
  to: string;
  linkLabel: string;
}) {
  return (
    <div className="mb-4 flex items-end justify-between gap-4 border-b border-line pb-3">
      <div className="flex items-center gap-3">
        <span className="h-px w-6 bg-accent/50" aria-hidden="true" />
        <h2 className="font-display text-lg text-ink">{title}</h2>
      </div>

      <Link
        to={to}
        className="shrink-0 text-xs tracking-wide text-muted underline-offset-4 transition-colors hover:text-accent hover:underline"
      >
        {linkLabel}
      </Link>
    </div>
  );
}
